import { STATUS, clearFuturePastFlags, isTerminal } from "./model.js";
import { fmtPct, sideChanceText } from "./formatPct.js";
import type { Entry, ForecastEvent, Role, Run, RunProgress, Status } from "./model.js";
import type { Conversation, CustomEventPopover, RunState } from "./reducer.js";
import { liveStatus, projectLiveEvents, projectLiveHeadline } from "./liveTriggers.js";
import { SEED_FAILED_TEXT, SEED_STARTED_TEXT } from "./seed.js";
import { userFacingRunError } from "./runErrorText.js";
import { streamsResearch } from "./deepProgress.js";
import { showsRunStatus } from "./progressSteps.js";
import type { Headline } from "./progressView.js";

export type ChatProjectionItem =
  | {
      kind: "entry";
      role: Role;
      entryKind: string | null;
      content: string;
      ts: string;
      metadata: Record<string, unknown>;
    }
  | {
      kind: "event";
      runId: string;
      status: Status;
      progress: RunProgress | null;
      engine: string | null;
      ts: string;
      terminal: boolean;
      content: string | null;
      /** Raw error text when `content` replaced it with friendly wording (debug only). */
      detail?: string | null;
    }
  | {
      kind: "narration";
      runId: string;
      ordinal: number;
      phase: string | null;
      ts: string;
      content: string;
    };

export type MergedChatProjectionItem = ChatProjectionItem & { _laneIdx: number; _rootId: string };

// Sibling order stays identical to branchYs geometry order so a lane's flat branch list
// aligns with it by index.
const _bySiblingOrder = (a: Run, b: Run): number =>
  a.createdAt < b.createdAt
    ? -1
    : a.createdAt > b.createdAt
      ? 1
      : a.runId < b.runId
        ? -1
        : a.runId > b.runId
          ? 1
          : 0;

/**
 * Which slice (owner or shared) a runId lives in. The slices are provably disjoint
 * (`GET /api/forecasts/shared` filters `user_id <> $me` server-side), so this is a partition
 * lookup, not a precedence rule. Hardcoding `state.runsById` for a possibly-shared runId
 * silently yields an empty tree for shared rows.
 */
// `sharedRunsById` reads are optional-chained: every REAL RunState carries it
// (initialState/reducer), but a hand-built partial state object in a test
// that predates this field shouldn't crash on a lookup miss it never cared
// about — it should just behave as if the shared slice is empty.
const _sliceFor = (state: RunState, runId: string): Record<string, Run> =>
  state.runsById[runId] ? state.runsById : (state.sharedRunsById ?? {});

export const selectRun = (state: RunState, runId: string): Run | null =>
  state.runsById[runId] || state.sharedRunsById?.[runId] || null;

export const selectActiveRun = (state: RunState): Run | null =>
  state.activeRunId ? selectRun(state, state.activeRunId) : null;

/**
 * The roots of the forecasts on screen: the selected run's root, then each compared root, each
 * once. A root is the run a branch's `conversationId` names, or the run itself.
 */
export function selectOpenRootIds(state: RunState): string[] {
  const ids = new Set<string>();
  const active = selectActiveRun(state);
  if (active) ids.add(active.conversationId || active.runId);
  for (const id of state.comparison?.rootIds ?? []) ids.add(id);
  return [...ids];
}

/** `runId`'s open custom-event popover, or null; never one whose run is gone. */
export const selectCustomEventPopover = (
  state: RunState,
  runId: string | null | undefined,
): CustomEventPopover | null =>
  runId && selectRun(state, runId) ? (state.customEventPopovers?.[runId] ?? null) : null;

/**
 * Scanned for `forecastId` lookups, where no map can be indexed. The slices are disjoint
 * (see {@link _sliceFor}), so the owner-first order is belt-and-braces.
 */
export const selectAllRuns = (state: RunState): Run[] => [
  ...Object.values(state.runsById),
  ...Object.values(state.sharedRunsById ?? {}),
];

/**
 * DELIBERATELY `runsById`-ONLY, never falling back to `sharedRunsById`. Resume/reattach applies
 * only to runs the caller's own session started; a shared row belongs to its owner's session.
 */
export const selectRunByTraceId = (state: RunState, traceId: string | null): Run | null =>
  traceId ? Object.values(state.runsById).find((r) => r.traceId === traceId) || null : null;

/**
 * DELIBERATELY `runsById`-ONLY, for the same reason as {@link selectRunByTraceId}: the resume
 * sweep only concerns runs this session might reattach to.
 */
export const selectRunsByStatus = (state: RunState, status: Status): Run[] =>
  Object.values(state.runsById).filter((r) => r.status === status);

export const selectBranches = (state: RunState, rootRunId: string): Run[] =>
  Object.values(_walkableFor(state, rootRunId)).filter((r) => r.parentRunId === rootRunId);

/**
 * Parentage is acyclic by construction (a parent is minted before its child, mirroring the DB
 * `parent_forecast_id` chain). A `visited` guard still protects against malformed parent
 * pointers.
 */
export const selectRunTree = (state: RunState, rootRunId: string): Run[] => {
  // The child index is built once so the DFS avoids an O(N) scan at every node.
  //
  // A shared root's subtree spans BOTH maps (see `_walkableFor`): a branch the viewer creates
  // on a forecast shared with them is their own row in `runsById`.
  const slice = _walkableFor(state, rootRunId);
  return _collectSubtree(slice, rootRunId, _childIndexFor(slice));
};

export function selectBranchRuns(state: RunState, rootId: string | null): Run[] {
  return rootId ? selectRunTree(state, rootId).slice(1) : [];
}

/**
 * Premise derivation needs both slices because a branch's parent events and minted-trigger
 * evidence live in whichever slice holds that tree. The owner slice wins a collision, like the
 * reducer.
 */
export function selectPremiseRunsById(state: RunState): Record<string, Run> {
  return { ...(state.sharedRunsById || {}), ...(state.runsById || {}) } as Record<string, Run>;
}

/**
 * The branches still in play for RE-FORECASTING. A branch cleared on a settled premise is
 * deliberately kept, since a confirmed premise stays tracked, so this differs from
 * `selectVisibleBranchRuns`.
 */
export function selectUnarchivedBranchRuns(state: RunState, rootId: string | null): Run[] {
  const archived = new Set<string>();
  return selectBranchRuns(state, rootId).filter((r) => {
    const out = !!r.clearedAt || (r.parentRunId != null && archived.has(r.parentRunId));
    if (out) archived.add(r.runId);
    return !out;
  });
}

/**
 * Every branch the timeline's "?" marks read (withScenarioOutcomes): cleared ones included, since
 * clearing a scenario does not settle its trigger. At a past mark (`atMs`, ms), only those created
 * by then, the same cutoff as `selectVisibleBranchRuns`.
 */
export function selectScenarioRuns(
  state: RunState,
  rootId: string | null,
  atMs: number | null = null,
): Run[] {
  const all = selectBranchRuns(state, rootId);
  if (atMs == null) return all;
  return all.filter((r) => {
    const created = typeof r.createdAt === "string" ? Date.parse(r.createdAt) : NaN;
    return !(Number.isFinite(created) && created > atMs);
  });
}

/**
 * The branches a LANE should draw. At a past mark (`atMs` = that moment, ms) the tree is shown
 * as it then stood, including a branch cleared since and excluding one forked later.
 *
 * Filtered here rather than in the query because a cleared branch is ARCHIVED, not deleted: the
 * row keeps travelling on the wire so its conditional forecast stays scoreable and a rewind can
 * still render it.
 */
export function selectVisibleBranchRuns(
  state: RunState,
  rootId: string | null,
  atMs: number | null = null,
): Run[] {
  const ms = (iso: unknown) => {
    const t = typeof iso === "string" ? Date.parse(iso) : NaN;
    return Number.isFinite(t) ? t : null;
  };
  const clearedBy = (iso: unknown) => {
    const t = ms(iso);
    return t != null && (atMs == null || t <= atMs);
  };
  // The subtree walk is depth-first (parents before children), so a child can read whether
  // its parent was hidden.
  const hidden = new Set<string>();
  return selectBranchRuns(state, rootId).filter((r) => {
    const premise = r.premise as { clearedAt?: string | null } | null | undefined;
    const created = ms(r.createdAt);
    const hide =
      (r.parentRunId != null && hidden.has(r.parentRunId)) ||
      clearedBy(r.clearedAt) ||
      clearedBy(premise?.clearedAt) ||
      (atMs != null && created != null && created > atMs);
    if (hide) hidden.add(r.runId);
    return !hide;
  });
}

/**
 * The one live run in `activeRunId`'s tree that a cancel should target. Shared by App's
 * `canCancel` gate and the `cancelForecast` command, which adds traceId fallbacks for a
 * reattached/reloaded root, so its cancelability is a superset of this.
 *
 * Explicitly owner-only: `selectRunTree` is slice-aware and would resolve a shared root's
 * subtree, so this guard keeps a shared row from ever becoming cancellable through selectors.
 */
export const selectCancellableRun = (state: RunState, activeRunId: string | null): Run | null =>
  activeRunId && state.runsById[activeRunId]
    ? selectRunTree(state, activeRunId).find(
        // `!r.awaitingTrace`: a manual reforecast is RUNNING from the click, before its own
        // trace exists, and until then `r.traceId` is the PREVIOUS run's. Cancelling would
        // aim the server call at an already-finished trace — and a reforecast is
        // non-abortable anyway, so the run would be marked CANCELLED while work continued.
        (r) => r.status === STATUS.RUNNING && r.traceId && !r.awaitingTrace,
      ) || null
    : null;

export const selectConversationForRun = (state: RunState, runId: string): Conversation | null => {
  const run = selectRun(state, runId);
  if (!run) return null;
  return state.conversationsById[run.conversationId] || null;
};

/**
 * DELIBERATELY `runsById`-ONLY: comparison is owner-only, so a shared row's id in
 * `comparison.rootIds` resolves to nothing rather than becoming a lane.
 */
export const selectComparisonLanes = (state: RunState): Run[] =>
  (state.comparison?.rootIds ?? []).map((id) => state.runsById[id] || null).filter(Boolean);

/**
 * Derived view only: the source of truth stays per-conversation and state is never mutated.
 * DELIBERATELY `runsById`-ONLY, like {@link selectComparisonLanes}.
 */
export const selectMergedComparisonView = (
  state: RunState,
  rootIds: string[],
): Array<Entry & { _laneIdx: number; _rootId: string }> => {
  const tagged = rootIds.flatMap((rootId, laneIdx) => {
    const run = state.runsById[rootId];
    const conv = run ? state.conversationsById[run.conversationId] : null;
    const entries = conv ? conv.entries : [];
    return entries.map((e) => ({ ...e, _laneIdx: laneIdx, _rootId: rootId }));
  });
  // Intra-lane equal-ts order is the conversation's own order, preserved because the sort is
  // stable (ES2019+).
  return tagged.sort((a, b) => (a.ts < b.ts ? -1 : a.ts > b.ts ? 1 : a._laneIdx - b._laneIdx));
};

/**
 * True while a run's question is still the raw typed text: preprocess has not returned, so
 * the run has no contract or only submit's placeholder one.
 */
export const awaitingPreprocess = (
  run: { status?: string; contract?: { placeholder?: unknown } | null } | null,
): boolean =>
  !!run &&
  run.status === STATUS.PREPROCESSING &&
  (!run.contract || run.contract.placeholder === true);

const isPending = (status: string): boolean =>
  status === STATUS.PREPROCESSING || status === STATUS.RUNNING;

export type LaneRun = Run & { progressLiveStatus?: string | null };
const _liveEventCache = new WeakMap<object, Map<string, ForecastEvent[]>>();
const _laneRunCache = new WeakMap<Run, { dayKey: string; value: LaneRun }>();

// Date-only live events cross `isPast` at local midnight (`eventEndMs`), so the
// projection caches must roll over at that same boundary rather than UTC midnight.
export const projectionDayKey = (now: Date): string =>
  `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

/** Rebuild a cached display day at viewer-local noon, safely away from midnight rollover. */
export const projectionDateAtLocalNoon = (dayKey: string): Date => {
  const [year, month, day] = dayKey.split("-").map(Number);
  return new Date(year!, month! - 1, day!, 12);
};

export function projectRunForLane(
  run: Run,
  now: Date = new Date(),
  enabled: boolean = true,
): LaneRun {
  if (!enabled || !isPending(run.status) || !run.progressDoc) return run;
  const dayKey = projectionDayKey(now);
  const cached = _laneRunCache.get(run);
  if (cached?.dayKey === dayKey) return cached.value;
  const doc = run.progressDoc;
  let events: ForecastEvent[] | null = run.events;
  if (events === null || events.length === 0) {
    let byDay = _liveEventCache.get(doc);
    events = byDay?.get(dayKey) ?? null;
    if (!events) {
      events = projectLiveEvents(doc, now);
      if (!byDay) {
        byDay = new Map();
        _liveEventCache.set(doc, byDay);
      }
      byDay.set(dayKey, events);
    }
  }
  const headline = projectLiveHeadline(doc);
  const projected: LaneRun = {
    ...run,
    contract: headline ? { ...(run.contract ?? {}), ...headline } : run.contract,
    events,
    progressLiveStatus: liveStatus(doc, run.progressActivity, run.progressTailReport),
  };
  _laneRunCache.set(run, { dayKey, value: projected });
  return projected;
}
/** A timeline lane: its root run and the branch runs drawn under it. */
export interface Lane<R extends Run = LaneRun> {
  root: R;
  branches: R[];
}

const withFreshPast = <R extends Run>(run: R, now: Date): R => {
  const events = clearFuturePastFlags(run.events, now);
  return events === run.events ? run : { ...run, events };
};

/**
 * Re-checks every lane run's stored `isPast` against `now` (see clearFuturePastFlags):
 * hydrated rows keep the flag stamped when they were saved. Returns the input array, and each
 * unchanged lane, by identity so a memo over it stays stable.
 */
export function refreshLanePastFlags<R extends Run, L extends Lane<R>>(lanes: L[], now: Date): L[] {
  let changed = false;
  const out = lanes.map((lane) => {
    const root = withFreshPast(lane.root, now);
    const branches = lane.branches.map((b) => withFreshPast(b, now));
    if (root === lane.root && branches.every((b, i) => b === lane.branches[i])) return lane;
    changed = true;
    return { ...lane, root, branches };
  });
  return changed ? out : lanes;
}

/**
 * Whether a run has a report to open. A manual reforecast reuses the completed run (status back
 * to RUNNING, or ERRORED if it fails) but keeps the previous result until the new one lands, so a
 * prior report or explanation counts as well as a COMPLETED status.
 */
export function reportAvailable(run: Run | null | undefined): boolean {
  if (!run) return false;
  if (run.status === STATUS.COMPLETED) return true;
  return (
    run.report != null ||
    (typeof run.serverExplanation === "string" && run.serverExplanation !== "")
  );
}

const isBoundedProbability = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 1;
const currentRunProbability = (run: Run): number | null => {
  if (isBoundedProbability(run.serverPrediction)) return run.serverPrediction;
  return isBoundedProbability(run.contract?.probability) ? run.contract.probability : null;
};
// Clone each event OBJECT (not just the array): render mutates events in place
// (e.g. `e.isPast = …`), so returning shared objects would leak that mutation
// back into store state. Shallow clone is enough (the mutated fields are top-level).
const cloneEvents = (events: ForecastEvent[] | null): ForecastEvent[] | null =>
  events ? events.map((e) => ({ ...e })) : events;

const _buildChildIndex = (runsById: Record<string, Run>): Map<string, Run[]> => {
  const idx = new Map<string, Run[]>();
  for (const run of Object.values(runsById)) {
    if (run.parentRunId != null) {
      if (!idx.has(run.parentRunId)) idx.set(run.parentRunId, []);
      idx.get(run.parentRunId)!.push(run);
    }
  }
  for (const children of idx.values()) {
    children.sort(_bySiblingOrder);
  }
  return idx;
};

// A SHARED root's subtree spans BOTH maps: a branch the viewer creates on a forecast shared
// with them is their own row, so it lands in `runsById` while its parent stays in
// `sharedRunsById`. The owner wins a conflict. The reducer produces a new map only when the
// store changes, so this WeakMap cache, keyed on the identity of both maps, never goes stale.
const _mergedRunsCache = new WeakMap<
  Record<string, Run>,
  WeakMap<Record<string, Run>, Record<string, Run>>
>();
const _mergedRuns = (
  owner: Record<string, Run>,
  shared: Record<string, Run>,
): Record<string, Run> => {
  let inner = _mergedRunsCache.get(owner);
  if (!inner) {
    inner = new WeakMap();
    _mergedRunsCache.set(owner, inner);
  }
  let merged = inner.get(shared);
  if (!merged) {
    merged = { ...shared, ...owner };
    inner.set(shared, merged);
  }
  return merged;
};

/**
 * The map to WALK a run's subtree in. Use this, not `_sliceFor`, for "what hangs off this run".
 */
const _walkableFor = (state: RunState, runId: string): Record<string, Run> => {
  const owner = state.runsById;
  if (owner[runId]) return owner;
  const shared = state.sharedRunsById;
  // No shared slice at all — a hand-built partial state, which this file
  // supports. Returning `owner` keeps both caches keyed on a stable identity; a
  // `?? {}` here would be a fresh WeakMap key on every call and rebuild the
  // merged map and its child index each time.
  if (!shared) return owner;
  return _mergedRuns(owner, shared);
};

const _childIndexCache = new WeakMap<Record<string, Run>, Map<string, Run[]>>();
const _childIndexFor = (runsById: Record<string, Run>): Map<string, Run[]> => {
  let idx = _childIndexCache.get(runsById);
  if (!idx) {
    idx = _buildChildIndex(runsById);
    _childIndexCache.set(runsById, idx);
  }
  return idx;
};

/**
 * The wide legacy-wire forecast shape App.jsx consumes. Typed as an open record because its
 * only consumer is untyped `App.jsx`, so exhaustive field typing buys nothing yet.
 */
export type LegacyForecast = Record<string, unknown>;

// Keyed on the run object: valid because the reducer produces a new run object only when that
// run changes. A changed branch is a new object in its ancestors' depSig, so their comparisons
// fail and they rebuild.
const _forecastCache = new WeakMap<
  Run,
  { depSig: Run[]; projectionDayKey: string | null; value: LegacyForecast }
>();

// The returned list doubles as the cache dependency signature.
const _collectSubtree = (
  runsById: Record<string, Run>,
  runId: string,
  childIdx: Map<string, Run[]>,
  _visited: Set<string> = new Set(),
): Run[] => {
  const root = runsById[runId];
  if (!root || _visited.has(runId)) return [];
  _visited.add(runId);
  const out = [root];
  for (const child of childIdx.get(runId) || []) {
    out.push(..._collectSubtree(runsById, child.runId, childIdx, _visited));
  }
  return out;
};

const _depSigEqual = <T>(a: readonly T[], b: readonly T[]): boolean =>
  a.length === b.length && a.every((r, i) => r === b[i]);

const _runToLegacyForecast = (
  run: Run | null | undefined,
  runsById: Record<string, Run>,
  childIdx: Map<string, Run[]>,
  liveProgressEnabled: boolean = true,
): LegacyForecast | null => {
  if (!run) return null;
  const now = new Date();
  const displayRun = projectRunForLane(run, now, liveProgressEnabled);
  const displayDayKey = displayRun === run ? null : projectionDayKey(now);
  const depSig = _collectSubtree(runsById, run.runId, childIdx);
  const cached = _forecastCache.get(run);
  if (cached && cached.projectionDayKey === displayDayKey && _depSigEqual(cached.depSig, depSig))
    return cached.value;
  const value: LegacyForecast = {
    id: run.runId,
    forecastId: run.forecastId,
    // Provenance and shared-row attribution must be projected here because the log-row
    // components read this legacy shape, not the Run; dropping them makes every read-only gate
    // on `f.isOwn` unreachable.
    isOwn: run.isOwn,
    owner: run.owner,
    isShared: run.isShared,
    othersWatching: run.othersWatching,
    // Dropped here, `canEdit(f)` falls to its absent-means-true default — right for an
    // owner-leg row, wrong as a stand-in for an answer the server did send.
    canEdit: run.canEdit,
    traceId: run.traceId,
    contractKey: run.contractKey ?? null,
    contractKeyStatus: run.contractKeyStatus ?? null,
    question: run.question,
    contract: displayRun.contract,
    events: cloneEvents(displayRun.events),
    serverContext: run.serverContext,
    status: run.status,
    createdAt: run.createdAt,
    // Grant time on a shared-leg row (null on the owner leg): the Forecast Manager's
    // merge sort key, so a forecast just shared with the viewer lands on top.
    receivedAt: run.receivedAt ?? null,
    schedule: run.schedule,
    // Append-only report-pointer history. Projected because App.jsx reads
    // active.reportPointers for the pointer series and the report overlay.
    reportPointers: run.reportPointers ?? [],
    _series: run.series,
    resolution: run.resolution,
    // Data-plane policy status (deleted/resolved/active/…). Projected to badge a RESOLVED
    // outcome and to keep a resolved-but-tracked forecast on the watchlist.
    contractStatus: run.contractStatus ?? null,
    // The viewer's alert prefs. Both slices map through here, so owned and shared rows alike
    // reach getTrackingPrefs and the re-watch email seed with the server value.
    notify: run.notify ?? {},
    // Trigger-mint lineage: marks machine-minted questions and identifies the parent forecast
    // to link back to.
    triggerOrigin: run.triggerOrigin ?? null,
    // The preprocessed question even before a lean row's contract backfills.
    contractQuestion: run.contractQuestion ?? null,
    // What list cards draw until the lean row's contract / history backfill.
    listSummary: run.listSummary ?? null,
    alert: run.alert,
    lastReforecastedAt: run.lastReforecastedAt,
    // When the current run began: dates a lean row's background rerun on its card (listSource).
    startedAt: run.startedAt ?? null,
    _debug: run.debug,
    _obs: run.obs,
    _partial: run.partial,
    // Reads the raw run because a live projection carries no estimate, so a pending reforecast
    // keeps the prior run's headline until its own result lands.
    serverPrediction: currentRunProbability(run),
    progressSeed: displayRun.progressSeed,
    progressLiveStatus: displayRun.progressLiveStatus ?? null,
    serverExplanation: run.serverExplanation ?? run.serverContext?.explanation ?? null,
    report: run.report ?? run.serverContext?.report ?? null,
    _engine: run.backend ?? run.serverContext?.metadata?.orchestrator ?? null,
    _pending: isPending(run.status),
    // Lean-hydrated, detail backfill still owed. Projected because the save
    // effect gates on it: persisting such a row would upsert nulls over the very
    // columns the lean projection omitted (contract/events/server_expl/server_ctx).
    detailPending: run.detailPending === true,
    // Provenance for that gate: whether this row's detail came from a LOCAL completion and
    // which blobs it wrote. The persister reads this projection, not the raw run.
    detailLocal: run.detailLocal === true,
    detailLocalFields: run.detailLocalFields,
    // Complete-on-creation identity. The persister uses this projection to keep
    // derived rows off the generic content-save/contract-allocation path.
    forkDerived: run.forkDerived,
    history: run.history, // reforecast snapshot trail (probability-over-time chart)
    // Only when set, so every other row's saved snapshot is unchanged.
    ...(run.vintageRestored ? { vintageRestored: true } : {}),
    move: run.move, // server-sourced watchlist move signal (fallback computed client-side)
    // A completed root shows RUNNING while any branch is live; this replaces the legacy nested
    // branches[] array.
    _branchRunning: depSig.slice(1).some((b) => isPending(b.status)),
  };
  _forecastCache.set(run, { depSig, projectionDayKey: displayDayKey, value });
  return value;
};

/**
 * Legacy-shape conversion for a run object the caller already holds. The map to walk is resolved
 * via {@link _walkableFor} because a run from `selectActiveRun` may be a shared row, and
 * building its subtree index from the wrong map silently yields an empty branch list.
 */
export const runToLegacyForecast = (
  run: Run | null | undefined,
  state: RunState,
  liveProgressEnabled: boolean = true,
) => {
  if (!run) return null;
  const slice = _walkableFor(state, run.runId);
  return _runToLegacyForecast(run, slice, _childIndexFor(slice), liveProgressEnabled);
};

/**
 * Upper bound on how many root forecasts the display list carries. It is a render-cost
 * backstop, not a product limit. It is deliberately not unbounded, because every root is mapped
 * through `_runToLegacyForecast` (which walks each subtree) on every store change; if accounts
 * approach it, the fix is windowed/virtualized rendering, not a smaller data cap.
 */
export const FORECAST_DISPLAY_CAP = 1000;

// Stable array reference for selectLegacyForecasts: if all mapped outputs are
// the same references as last time, return the previous array to avoid
// triggering downstream useMemo/useEffect that depend on the forecasts array.
let _lastForecastOutputs: Array<LegacyForecast | null> | null = null;
let _lastForecastsArray: Array<LegacyForecast | null> | null = null;

export const selectLegacyForecasts = (
  state: RunState,
  liveProgressEnabled: boolean = true,
): Array<LegacyForecast | null> => {
  const childIdx = _buildChildIndex(state.runsById);
  // Newest received first: a copy of one of Sayer's forecasts is dated by its forecast but
  // arrived when the user opened it, so it sorts (and survives the cap) by that.
  const at = (r: Run) => r.receivedAt ?? r.createdAt;
  const roots = Object.values(state.runsById)
    .filter((r) => r.parentRunId == null)
    .sort((a, b) =>
      at(a) < at(b) ? 1 : at(a) > at(b) ? -1 : a.runId < b.runId ? 1 : a.runId > b.runId ? -1 : 0,
    )
    .slice(0, FORECAST_DISPLAY_CAP);
  const mapped = roots.map((r) =>
    _runToLegacyForecast(r, state.runsById, childIdx, liveProgressEnabled),
  );
  // When _lastForecastOutputs is set, _lastForecastsArray was set in the same
  // pass below (they're written together), so the non-null assertion holds.
  if (_lastForecastOutputs && _depSigEqual(_lastForecastOutputs, mapped)) {
    return _lastForecastsArray!;
  }
  _lastForecastOutputs = mapped;
  _lastForecastsArray = mapped;
  return mapped;
};

let _lastSharedOutputs: Array<LegacyForecast | null> | null = null;
let _lastSharedArray: Array<LegacyForecast | null> | null = null;

/**
 * The "Shared with me" section's rows, sourced from `sharedRunsById` only and deliberately not
 * merged into `forecasts`: the shared lane is a separate feed rendered as its own third
 * section. There is no display cap (unlike `FORECAST_DISPLAY_CAP`) because pagination is
 * server-driven via `next_page_token`, so a client-side cap would fight the server's page size.
 */
export const selectSharedForecasts = (
  state: RunState,
  liveProgressEnabled: boolean = true,
): Array<LegacyForecast | null> => {
  const sharedRunsById = state.sharedRunsById ?? {};
  const childIdx = _buildChildIndex(sharedRunsById);
  const roots = Object.values(sharedRunsById)
    .filter((r) => r.parentRunId == null)
    .sort((a, b) =>
      a.createdAt < b.createdAt
        ? 1
        : a.createdAt > b.createdAt
          ? -1
          : a.runId < b.runId
            ? 1
            : a.runId > b.runId
              ? -1
              : 0,
    );
  const mapped = roots.map((r) =>
    _runToLegacyForecast(r, sharedRunsById, childIdx, liveProgressEnabled),
  );
  if (_lastSharedOutputs && _depSigEqual(_lastSharedOutputs, mapped)) {
    return _lastSharedArray!;
  }
  _lastSharedOutputs = mapped;
  _lastSharedArray = mapped;
  return mapped;
};

let _lastWatchlistOutputs: Array<LegacyForecast | null> | null = null;
let _lastWatchlistArray: Array<LegacyForecast | null> | null = null;

/**
 * Watchlisted forecasts sourced from the FULL store, not the capped display list, so watchlist
 * membership is independent of `FORECAST_DISPLAY_CAP`.
 *
 * The predicate runs on the raw run, because `isWatchlisted` reads `id ?? runId` /
 * `contractStatus` / `schedule`, all of which a Run already carries. Only the matches are mapped
 * through `_runToLegacyForecast`. Resolved-watchlisted rows (resolved contracts whose cadence
 * spec survived) are not counted by `handlePin`'s WATCHLIST_LIMIT, so they accumulate beyond it.
 */
export const selectWatchlistForecasts = (
  state: RunState,
  isWatchlisted: (f: unknown) => boolean,
  liveProgressEnabled: boolean = true,
): Array<LegacyForecast | null> => {
  const matches = Object.values(state.runsById).filter(
    (r) => r.parentRunId == null && isWatchlisted(r),
  );
  if (!matches.length) {
    if (_lastWatchlistOutputs && _lastWatchlistOutputs.length === 0) return _lastWatchlistArray!;
    _lastWatchlistOutputs = [];
    _lastWatchlistArray = [];
    return _lastWatchlistArray;
  }
  const childIdx = _buildChildIndex(state.runsById);
  const mapped = matches.map((r) =>
    _runToLegacyForecast(r, state.runsById, childIdx, liveProgressEnabled),
  );
  if (_lastWatchlistOutputs && _depSigEqual(_lastWatchlistOutputs, mapped)) {
    return _lastWatchlistArray!;
  }
  _lastWatchlistOutputs = mapped;
  _lastWatchlistArray = mapped;
  return mapped;
};

/**
 * Resolves one legacy forecast by `forecastId` or `runId`, scanning ALL root runs rather than
 * the capped display list, because a deep link may target a forecast older than the most recent
 * `FORECAST_DISPLAY_CAP`.
 *
 * DELIBERATELY `runsById`-ONLY so resume and compare never reach shared rows: resume would try
 * to reattach to a stream belonging to someone else's session, and compare would seat a lane
 * `selectComparisonLanes` refuses to draw. URL-driven focus is the one caller that must cross
 * the boundary; it has its own selector below.
 */
export const selectLegacyForecastById = (
  state: RunState,
  id: string | null | undefined,
  liveProgressEnabled: boolean = false,
): LegacyForecast | null =>
  _legacyForecastById(
    state,
    id,
    state.runsById[id ?? ""],
    () => Object.values(state.runsById),
    liveProgressEnabled,
  );

/**
 * Resolves across BOTH slices, for URL-driven focus and for the root of a branch being
 * reattached: a share URL names a forecast the viewer does not own, and a branch of a shared
 * forecast has its root in `sharedRunsById`. Every other restore stays owner-only (see its
 * sibling).
 */
export const selectFocusableForecastById = (
  state: RunState,
  id: string | null | undefined,
  liveProgressEnabled: boolean = false,
): LegacyForecast | null =>
  _legacyForecastById(
    state,
    id,
    id ? selectRun(state, id) : null,
    () => selectAllRuns(state),
    liveProgressEnabled,
  );

/**
 * The branch run a `?forecast=` id names, by runId or forecastId, across both slices; null when
 * the id names a root or nothing in the store. {@link selectFocusableForecastById} resolves roots
 * only, so a URL caller asks this when that misses, to tell "a branch the UI cannot focus" apart
 * from "not in the store" (SOO-862).
 */
export const selectLinkedBranchRun = (
  state: RunState,
  id: string | null | undefined,
): Run | null => {
  if (!id) return null;
  const direct = selectRun(state, id);
  if (direct) return direct.parentRunId != null ? direct : null;
  return selectAllRuns(state).find((r) => r?.parentRunId != null && r.forecastId === id) ?? null;
};

/**
 * The focusable root forecast a branch belongs to, following `parentRunId` up through nested
 * branches; null when that root is not in the store (the viewer cannot see it). A `seen` guard
 * stops on a malformed parent cycle.
 */
export const selectBranchRootForecast = (
  state: RunState,
  branch: Run | null | undefined,
  liveProgressEnabled: boolean = false,
): LegacyForecast | null => {
  const rootRunId = branch ? selectRootRunId(state, branch.runId) : null;
  return rootRunId ? selectFocusableForecastById(state, rootRunId, liveProgressEnabled) : null;
};

/**
 * The runId of the root `runId` belongs to (itself for a root), following `parentRunId` across
 * both slices; null when the run or any ancestor is not in the store, or on a parent cycle.
 */
export const selectRootRunId = (state: RunState, runId: string): string | null => {
  let run = selectRun(state, runId);
  const seen = new Set<string>();
  while (run?.parentRunId != null) {
    if (seen.has(run.runId)) return null;
    seen.add(run.runId);
    run = selectRun(state, run.parentRunId);
  }
  return run ? run.runId : null;
};

/**
 * `scan` is a thunk so a direct hit never allocates a run list. A branch direct-hit
 * (`parentRunId != null`) falls through to the root-only scan, which cannot match a branch
 * runId, yielding null.
 */
function _legacyForecastById(
  state: RunState,
  id: string | null | undefined,
  direct: Run | null | undefined,
  scan: () => Run[],
  liveProgressEnabled: boolean,
): LegacyForecast | null {
  if (!id) return null;
  const run =
    direct != null && direct.parentRunId == null
      ? direct
      : scan().find((r) => r != null && r.parentRunId == null && r.forecastId === id);
  if (!run) return null;
  const slice = _walkableFor(state, run.runId);
  return _runToLegacyForecast(run, slice, _childIndexFor(slice), liveProgressEnabled);
}

export const selectLegacyActive = (
  state: RunState,
  liveProgressEnabled: boolean = true,
): LegacyForecast | null => runToLegacyForecast(selectActiveRun(state), state, liveProgressEnabled);

// Return the store's comparison object directly — same shape ({ rootIds }) but stable
// reference when comparison hasn't changed, so React effects/memos that depend on
// `comparison` don't re-run on unrelated runState updates (e.g. progress ticks).
export const selectLegacyComparison = (state: RunState) => state.comparison;

// The seed's two chat beats ride the narration lane rather than a kind of
// their own — same renderer, same "gone once terminal" rule, same placement
// under the run's line. They take NEGATIVE ordinals so they sort above every
// wire beat (the server's start at 0) and can never collide with one; they are
// derived on each call and never enter `run.narration`, whose dedupe guard
// assumes a monotonic server-assigned ordinal.
const SEED_STARTED_ORDINAL = -2;
const SEED_OUTCOME_ORDINAL = -1;

const seedHeadlineText = (headline: Headline | null): string | null => {
  if (!headline) return null;
  if (headline.kind === "probability") return sideChanceText(headline.value);
  // A distribution's headline is its leading outcome: the whole vector is on
  // the canvas already, and a chat line that listed every slot would bury the
  // one fact a reader wants while the research is still running.
  let best = -1;
  let bestIdx = -1;
  headline.mass.forEach((m, i) => {
    if (m > best) {
      best = m;
      bestIdx = i;
    }
  });
  const label = headline.labels[bestIdx];
  return bestIdx < 0 || !label ? null : `${label} ${fmtPct(best)}%`;
};

/**
 * The seed's chat beats for one live run. Derived rather than appended, so the outcome beat
 * rewrites itself as the seed's number and then its summary arrive.
 *
 * Nothing here reads the producer's tool title: see `seed.ts` for why.
 */
const seedBeats = (run: Run): ChatProjectionItem[] => {
  if (!run.progressSeedStarted) return [];
  const ts = run.lastReforecastedAt || run.createdAt;
  const beat = (ordinal: number, content: string): ChatProjectionItem => ({
    kind: "narration",
    runId: run.runId,
    ordinal,
    // No phase label. The renderer prints `phase` as a visible "<phase> ·"
    // prefix, and "seed" is our word for this, not the reader's — the beats
    // below already say what is happening in plain English.
    phase: null,
    ts,
    content,
  });
  const beats = [beat(SEED_STARTED_ORDINAL, SEED_STARTED_TEXT)];
  const outcome = run.progressSeedOutcome;
  if (!outcome) return beats;
  if (!outcome.ok) return [...beats, beat(SEED_OUTCOME_ORDINAL, SEED_FAILED_TEXT)];
  const headline = seedHeadlineText(outcome.headline);
  // ok with no number yet: the forecast frame is milliseconds behind the
  // finish, so holding the started beat alone for that gap beats flashing a
  // bare "Initial forecast:" with nothing after it.
  if (!headline) return beats;
  const summary = outcome.summary?.trim();
  return [
    ...beats,
    beat(
      SEED_OUTCOME_ORDINAL,
      summary ? `Initial forecast: ${headline} — ${summary}` : `Initial forecast: ${headline}`,
    ),
  ];
};

// Last-result cache for selectChatProjection, which runs on every progress tick: a stable
// result avoids churning downstream memo/effect deps. The key is the rootRunId plus the
// conversation `entries` array reference and the ordered run objects from selectRunTree, whose
// references change when the reducer updates them.
let _lastChatProjKey: string | null = null;
let _lastChatProjDeepPanel: boolean | null = null;
let _lastChatProjEntries: readonly Entry[] | null = null;
let _lastChatProjTree: Run[] | null = null;
let _lastChatProjResult: readonly ChatProjectionItem[] | null = null;
// Shared frozen empty array for the missing-root / missing-conversation paths.
// A fresh `[]` literal each call is a new reference, which defeats downstream
// memoization and forces consumer re-renders; returning one stable ref avoids it.
const EMPTY = Object.freeze([]);

const fmtDuration = (ms: number | undefined): string => {
  if (typeof ms !== "number" || !isFinite(ms) || ms <= 0) return "";
  const s = ms / 1000;
  // Decide the branch on the ROUNDED value, matching how each branch renders it:
  // under a minute rounds to tenths (so 59.96s → 60.0 crosses into the minute
  // branch instead of rendering a bogus "60.0s"); at/over a minute rounds to
  // whole seconds first so the remainder is always 0–59 (never "1m 60s", e.g.
  // 119.6s → "2m 0s").
  const tenths = Math.round(s * 10) / 10;
  if (tenths < 60) return `${tenths.toFixed(1)}s`;
  const total = Math.round(s);
  return `${Math.floor(total / 60)}m ${total % 60}s`;
};

/**
 * Whether a run's progress is on the run status panel rather than in the chat: a first root run
 * that streams its research, whose panel stays open until it finishes (progressSteps.js
 * showsRunStatus).
 */
const panelCarriesProgress = (run: Run): boolean =>
  run.parentRunId == null && streamsResearch(run) && showsRunStatus(run, { liveProgress: true });

/**
 * The chat render list for a root run's conversation. Event lines are derived from run state,
 * never stored as entries, so a reload reconstructs them identically from the hydrated
 * `last_*` progress. `deepPanel` is true only where the run status card carries a research
 * run's progress (live progress on, not compare mode); elsewhere that progress stays in the chat.
 */
export const selectChatProjection = (
  state: RunState,
  rootRunId: string,
  { deepPanel = false }: { deepPanel?: boolean } = {},
): readonly ChatProjectionItem[] => {
  // Resolve through `selectRun`, not `state.runsById`: hardcoding the owner slice makes a
  // shared recipient's chat panel render blank (an empty result, not an error).
  const root = selectRun(state, rootRunId);
  if (!root) return EMPTY;
  const conv = state.conversationsById[root.conversationId];
  const entriesSrc = conv ? conv.entries : EMPTY;
  const tree = selectRunTree(state, rootRunId);
  if (
    _lastChatProjResult !== null &&
    _lastChatProjKey === rootRunId &&
    _lastChatProjDeepPanel === deepPanel &&
    _lastChatProjEntries === entriesSrc &&
    _lastChatProjTree !== null &&
    _depSigEqual(_lastChatProjTree, tree)
  ) {
    return _lastChatProjResult;
  }
  const entries: ChatProjectionItem[] = entriesSrc.map((e) => ({
    kind: "entry",
    role: e.role,
    entryKind: e.kind,
    content: e.content,
    ts: e.ts,
    metadata: e.metadata,
  }));
  const events: ChatProjectionItem[] = tree.map((run) => {
    const terminal = isTerminal(run.status);
    let content: string | null = null;
    let detail: string | null = null;
    if (!terminal) {
      // Non-terminal placeholder, phase-aware so the run line never reads blank. Branches and
      // compare legs start RUNNING and skip the preprocess phases; the contract-question phase
      // is a brief window; after a reload, progress hydrates from last_*.
      if (run.status === STATUS.PREPROCESSING) {
        content = run.contract
          ? run.contract.question || run.contract.title || "Forecasting…"
          : "Checking your question…";
      } else if (deepPanel && panelCarriesProgress(run)) {
        content = "Forecasting…";
      } else {
        content = run.progress?.message || "Forecasting…";
      }
    } else {
      if (run.status === STATUS.COMPLETED) {
        const currentProbability = currentRunProbability(run);
        const prob = currentProbability == null ? null : sideChanceText(currentProbability);
        // The duration suffix rides on EVERY completed line, including multiway forecasts whose
        // scalar probability is null, because the duration belongs to the run, not the answer's
        // shape.
        const dur = fmtDuration(run.obs?.latencyMs);
        const durSuffix = dur ? ` (${dur})` : "";
        if (run.parentRunId != null) {
          const label = run.modification ? `Scenario: ${run.modification}` : "Scenario";
          content =
            prob != null ? `${label} — Forecast: ${prob}${durSuffix}` : `${label}${durSuffix}`;
        } else {
          content = prob != null ? `Forecast: ${prob}${durSuffix}` : `Forecast ready${durSuffix}`;
        }
      } else if (run.status === STATUS.ERRORED) {
        content = userFacingRunError(run.error, run.errorCode);
        if (run.error && content !== run.error) detail = run.error;
      } else if (run.status === STATUS.CANCELLED) {
        content = run.modification
          ? `Scenario cancelled: ${run.modification}`
          : "Forecast cancelled";
      }
    }
    return {
      kind: "event",
      runId: run.runId,
      status: run.status,
      progress: run.progress ?? null,
      engine: run.backend ?? null,
      // A reforecast reuses the original run (old createdAt), so ts must use lastReforecastedAt
      // or its live lines sort to the top of the chat.
      ts: run.lastReforecastedAt || run.createdAt,
      terminal,
      content,
      ...(detail ? { detail } : {}),
    };
  });
  // The server stores every beat (keyed by trace_id + ordinal) and replays them on reattach, so
  // they are not derived-and-lost. They are hidden once the run is terminal because stale
  // commentary below a finished forecast looks as though it were still working.
  const narration: ChatProjectionItem[] = tree.flatMap((run) =>
    isTerminal(run.status) || (deepPanel && panelCarriesProgress(run))
      ? []
      : [
          ...seedBeats(run),
          ...run.narration.map((beat) => ({
            kind: "narration" as const,
            runId: run.runId,
            ordinal: beat.ordinal,
            phase: beat.phase,
            // Beats carry no timestamp of their own; anchoring them to the run's
            // chat-line position keeps them with their forecast, and the ordinal
            // tiebreak below holds them in arrival order.
            ts: run.lastReforecastedAt || run.createdAt,
            content: beat.text,
          })),
        ],
  );
  // At equal ts, entries precede events (a forecast's seed user turn precedes its run line),
  // and narration sorts after both, in ordinal order.
  const order = (it: ChatProjectionItem) => (it.kind === "entry" ? 0 : it.kind === "event" ? 1 : 2);
  const result: ChatProjectionItem[] = [...entries, ...events, ...narration].sort((a, b) =>
    a.ts < b.ts
      ? -1
      : a.ts > b.ts
        ? 1
        : order(a) !== order(b)
          ? order(a) - order(b)
          : a.kind === "narration" && b.kind === "narration"
            ? a.ordinal - b.ordinal
            : 0,
  );
  _lastChatProjKey = rootRunId;
  _lastChatProjDeepPanel = deepPanel;
  _lastChatProjEntries = entriesSrc;
  _lastChatProjTree = tree;
  _lastChatProjResult = result;
  return result;
};

/**
 * Compare-mode chat projection: merges each leg's selectChatProjection, interleaved by ts. Only
 * the shared seed user question is de-duplicated. The engine prefix on each event line is the
 * SOLE per-leg attribution, so the progress message itself must NOT embed the engine name.
 *
 * @param {object} state
 * @param {string[]} rootIds  leg root runIds
 * @param {(engine: string) => string} [engineLabelFn]  resolves a raw engine id to its human
 *   label. Defaults to identity (raw id) when omitted.
 */
export const selectComparisonChatProjection = (
  state: RunState,
  rootIds: string[],
  engineLabelFn?: (engine: string) => string,
): MergedChatProjectionItem[] => {
  if (!Array.isArray(rootIds) || rootIds.length === 0) return [];
  const labelOf = typeof engineLabelFn === "function" ? engineLabelFn : (e: string) => e;
  const seenEntry = new Set<string>();
  const out: MergedChatProjectionItem[] = [];
  rootIds.forEach((rootId, laneIdx) => {
    for (const it of selectChatProjection(state, rootId, { deepPanel: false })) {
      if (it.kind === "entry") {
        // De-dupe ONLY the shared seed USER question. It's persisted once per leg
        // with each leg's own created_at, so it appears in both legs' projections.
        // Restrict to role==='user' (and key by entryKind) so two legs producing an
        // identical assistant turn — a short ack, say — BOTH survive with their lane
        // attribution; only the genuinely-shared user turn collapses. Key ignores ts
        // (per-leg created_at differs, so a ts-sensitive key would never match).
        if (it.role === "user") {
          const key = `${it.role}|${it.entryKind || ""}|${it.content}`;
          if (seenEntry.has(key)) continue;
          seenEntry.add(key);
        }
        out.push({ ...it, _laneIdx: laneIdx, _rootId: rootId });
      } else if (it.kind === "narration") {
        // Narration carries no engine of its own; its lane attribution is the
        // `_laneIdx` every merged item already gets, and prefixing a model
        // sentence with an engine tag would read as part of the sentence.
        out.push({ ...it, _laneIdx: laneIdx, _rootId: rootId });
      } else {
        // Attribute the event line to its engine so two concurrent "Researching…"
        // (or two result) lines are distinguishable in the merged compare chat.
        // Uniform across EVERY event line from a root (live stream, resume, result)
        // — the prefix is the only attribution, so the message must carry no engine.
        const prefix = it.engine ? `[${labelOf(it.engine)}]: ` : "";
        out.push({
          ...it,
          _laneIdx: laneIdx,
          _rootId: rootId,
          content: it.content != null ? `${prefix}${it.content}` : it.content,
        });
      }
    }
  });
  const order = (it: MergedChatProjectionItem) => (it.kind === "entry" ? 0 : 1);
  return out.sort((a, b) =>
    a.ts < b.ts
      ? -1
      : a.ts > b.ts
        ? 1
        : order(a) - order(b) || (a._laneIdx ?? 0) - (b._laneIdx ?? 0),
  );
};
